import React, { lazy, Suspense, type JSX } from 'react';
import {
  createBrowserRouter,
  createRoutesFromElements,
  Route,
  RouterProvider,
  Outlet,
} from 'react-router-dom';

import { AuthRedirect, RoleProtectedRoute } from '@features/auth';
import { privateRoutes } from '@constants/privateRoutesConnect';
import { publicRoutes } from '@constants/publicRoutesConnect';
import { userType } from '@constants/userTypes';
import NotFoundPage from '@features/notFound/NotFoundPage.tsx';
import LoginForm from '@features/auth/components/LoginForm';
import MainLayout from '@ui/Layout/MainLayout/MainLayout';
import AgentLayout from '@ui/Layout/AgentLayout/AgentLayout';
import LoginLayout from '@ui/Layout/LoginLayout/LoginLayout';
import IntentCrud from '@features/intent/pages/IntentCrud.tsx';
import ScheduleSessionPage from '@features/scheduleSession/ScheduleSessionPage.tsx';
import AgentsPage from '@features/agent/Pages/AgentsPage';
import HomeTab from '@features/Home/HomeTab.tsx';
import CalendarPage from '@features/Calendar/pages/CalendarPage.tsx';
import VoicemailsDashboard from '@features/voicemail/pages/VoicemailsDashboard.tsx';
import { WizardPage, SessionDashboardPage } from '@features/wizard';
import ContactsDashboard from '@features/contacts/pages/ContactsDashboard';
import { DashboardPage, SessionDetailsPage } from '@features/dashboard';
import DashboardOverviewPage from '@features/dashboard/pages/DashboardOverviewPage';
import { OutreachPage } from '@features/Settings/pages/integrationManagementPages/OutreachPage.tsx';
import { SalesforcePage } from '@features/Settings/pages/integrationManagementPages/SalesforcePage.tsx';
import { HubspotPage } from '@features/Settings/pages/integrationManagementPages/HubspotPage.tsx';
import DashboardIntegrationsPage from '@features/dashboard/pages/DashboardIntegrationsPage.tsx';

const AdminConsoleTabs = lazy(
  async () =>
    await import('@features/AdminConsole/pages/AdminConsoleTabs.tsx'),
);
const SettingsTabs = lazy(
  async () =>
    await import('@features/Settings/pages/tabs/SettingsTabs.tsx'),
);

const Fallback = (
  <div
    className="w-full h-screen flex items-center justify-center bg-background text-muted-foreground"
    role="status"
    aria-label="Loading"
  >
    <div className="animate-spin w-8 h-8 border-4 border-primary border-t-transparent rounded-full" />
  </div>
);

const AppRouter = (): JSX.Element => {
  const router = createBrowserRouter(
    createRoutesFromElements(
      <>
        {/* Root Redirect */}
        <Route path="/" element={<AuthRedirect />} />

        {/* Login Layout */}
        <Route
          element={
            <LoginLayout>
              <Outlet />
            </LoginLayout>
          }
        >
          <Route path={publicRoutes.LOGIN} element={<LoginForm />} />
        </Route>

        {/* Agent Layout */}
        <Route
          element={
            <AgentLayout>
              <Outlet />
            </AgentLayout>
          }
        >
          <Route
            path={privateRoutes.AGENT}
            element={
              <RoleProtectedRoute allowedType={[userType.AGENT]}>
                <AgentsPage />
              </RoleProtectedRoute>
            }
          />
        </Route>

        {/* Layout ADMIN / SDR */}
        <Route
          element={
            <MainLayout>
              <Outlet />
            </MainLayout>
          }
        >
          <Route
            path={privateRoutes.HOME}
            element={
              <RoleProtectedRoute allowedType={[userType.ADMIN, userType.SDR]}>
                <HomeTab />
              </RoleProtectedRoute>
            }
          />
          <Route
            path={privateRoutes.CONTACTS}
            element={
              <RoleProtectedRoute allowedType={[userType.ADMIN, userType.SDR]}>
                <ContactsDashboard />
              </RoleProtectedRoute>
            }
          />
          <Route
            path={privateRoutes.VOICEMAIL}
            element={
              <RoleProtectedRoute allowedType={[userType.ADMIN, userType.SDR]}>
                <VoicemailsDashboard/>
              </RoleProtectedRoute>
            }
          />
          <Route
            path={privateRoutes.INTENT}
            element={
              <RoleProtectedRoute allowedType={[userType.ADMIN, userType.SDR]}>
                <IntentCrud />
              </RoleProtectedRoute>
            }
          />
          <Route
            path={privateRoutes.DASHBOARD_OVERVIEW}
            element={
              <RoleProtectedRoute allowedType={[userType.ADMIN, userType.SDR]}>
                <DashboardOverviewPage />
              </RoleProtectedRoute>
            }
          />
          <Route
            path={privateRoutes.DASHBOARD_SESSIONS}
            element={
              <RoleProtectedRoute allowedType={[userType.ADMIN, userType.SDR]}>
                <DashboardPage />
              </RoleProtectedRoute>
            }
          />
          <Route
            path={privateRoutes.DASHBOARD_SESSION}
            element={
              <RoleProtectedRoute allowedType={[userType.ADMIN, userType.SDR]}>
                <SessionDetailsPage />
              </RoleProtectedRoute>
            }
          />
          <Route
            path={privateRoutes.DASHBOARD_INTEGRATIONS}
            element={
              <RoleProtectedRoute allowedType={[userType.ADMIN, userType.SDR]}>
                <DashboardIntegrationsPage />
              </RoleProtectedRoute>
            }
          />
          <Route
            path={privateRoutes.CALENDAR}
            element={
              <RoleProtectedRoute allowedType={[userType.ADMIN, userType.SDR]}>
                <CalendarPage />
              </RoleProtectedRoute>
            }
          />
          <Route
            path={privateRoutes.ADMINCONSOLE}
            element={
              <RoleProtectedRoute allowedType={[userType.ADMIN]}>
                <AdminConsoleTabs />
              </RoleProtectedRoute>
            }
          />
          <Route
            path={privateRoutes.SETTINGS}
            element={
              <RoleProtectedRoute allowedType={[userType.ADMIN, userType.SDR]}>
                <SettingsTabs />
              </RoleProtectedRoute>
            }
          />
          <Route
            path={privateRoutes.OUTREACH}
            element={
              <RoleProtectedRoute allowedType={[userType.ADMIN, userType.SDR]}>
                <OutreachPage />
              </RoleProtectedRoute>
            }
          />
          <Route
            path={privateRoutes.SALESFORCE}
            element={
              <RoleProtectedRoute allowedType={[userType.ADMIN, userType.SDR]}>
                <SalesforcePage />
              </RoleProtectedRoute>
            }
          />
          <Route
            path={privateRoutes.HUBSPOT}
            element={
              <RoleProtectedRoute allowedType={[userType.ADMIN, userType.SDR]}>
                <HubspotPage />
              </RoleProtectedRoute>
            }
          />
          <Route
            path={privateRoutes.WIZARD}
            element={
              <RoleProtectedRoute allowedType={[userType.ADMIN, userType.SDR]}>
                <WizardPage />
              </RoleProtectedRoute>
            }
          />
          <Route
            path={privateRoutes.SESSION_DASHBOARD}
            element={
              <RoleProtectedRoute allowedType={[userType.ADMIN, userType.SDR]}>
                <SessionDashboardPage />
              </RoleProtectedRoute>
            }
          />
        </Route>

        <Route
          path={publicRoutes.SCHEDULESESSION}
          element={<ScheduleSessionPage />}
        />

        <Route path="*" element={<NotFoundPage />} />
      </>,
    ),
    { basename: '/workspace' },
  );

  return (
    <Suspense fallback={Fallback}>
      <RouterProvider router={router} />
    </Suspense>
  );
};

export default AppRouter;
